import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { IntervalGranularity } from '/imports/utils';
import { Doc } from '/imports/types';
import { ConsumptionMeter } from '/imports/api/consumptionMeters';

export interface ConsumptionInterval extends Doc {
  meterId: string;
  startTime: Date;
  endTime: Date;
  consumption: number;
  generation?: number;
  net?: number;
  resource?: string;
}

export const ConsumptionIntervals = new Mongo.Collection<ConsumptionInterval>('consumptionIntervals');

/**
 * What a consumption pull reports back. The account counts say how far it got; `filesProcessed` and
 * `filesFailed` count downloaded interval files that were ingested, which only the utilities that download
 * files (SDG&E) report. `queued` means the work went to the job queue and the counts are not known yet, so
 * the caller should say "started", not "finished".
 *
 * A pull used to return nothing and log its summary at info whatever happened, so a run that downloaded a
 * file and then failed to read a single row of it looked exactly like a run that worked. It hid a week of
 * failures in October 2026.
 */
export interface ConsumptionPullResult {
  queued: boolean;
  meters: number;
  utilityAccounts: number;
  skipped: number;         // accounts with nothing usable to pull with
  failed: number;          // accounts whose download threw
  filesProcessed: number;
  filesFailed: number;     // files that downloaded but could not be read
}

/** A pull that ran and has something the caller should be told about, rather than a silent success. */
export const consumptionPullHasProblems = (result?: ConsumptionPullResult): boolean =>
  !!result && !result.queued && (result.failed > 0 || result.filesFailed > 0 || result.skipped > 0);

/** A result that reports nothing, for a pull with no meters of that utility to walk. */
export const emptyConsumptionPullResult = (): ConsumptionPullResult =>
  ({ queued: false, meters: 0, utilityAccounts: 0, skipped: 0, failed: 0, filesProcessed: 0, filesFailed: 0 });

export const pullConsumptionIntervals = DeclareMethod<{ meterIds: string[], from: Date, to: Date, sync?: boolean }, ConsumptionPullResult>('pullConsumptionIntervals');
export const exportConsumptionIntervals = DeclareMethod<{ meterIds: string[], from: Date, to: Date, useUtc?: boolean }, string>('exportConsumptionIntervals');
/**
 * One bar of a consumption chart. Raw intervals and rollup rows both arrive in this shape, so the chart does
 * not care which collection answered — only `granularity`, which it labels the axis with.
 */
export interface ConsumptionIntervalBucket {
  startTime: Date;
  endTime: Date;
  consumption: number;
  generation?: number;
  net?: number;
}

export interface ConsumptionIntervalSeries {
  granularity: IntervalGranularity;
  intervals: ConsumptionIntervalBucket[];
}

export const getConsumptionIntervals = DeclareMethod<{ meterId: string, from: Date, to: Date }, ConsumptionIntervalSeries>('getConsumptionIntervals');
export const consumptionIntervalGapsReport = DeclareMethod<{ meterIds?: string[], from: Date, to: Date }, { meter: ConsumptionMeter, gaps: Date[][] }[]>('consumptionIntervalGapsReport');

/**
 * Hook for deriving extra interval fields before ingest; currently a passthrough.
 * @param interval - raw interval record
 * @returns the (possibly enriched) interval
 */
export const enrichInterval = <T extends { startTime: Date, endTime: Date, consumption: number, generation?: number, net?: number }>(interval: T) => {
  return interval;
};

/**
 * Upserts intervals for a meter, clamping negative consumption to zero.
 * @param intervals - intervals to ingest
 * @param meter - owning consumption meter
 */
export const ingestIntervals = async <T extends { startTime: Date, endTime: Date, consumption: number, generation?: number, net?: number }>(intervals: T[], meter: ConsumptionMeter) => {
  const meterId = meter._id;
  for (const interval of intervals) {
    const { startTime, endTime, consumption, generation, net } = enrichInterval(interval);
    const existing = await ConsumptionIntervals.findOneAsync({ meterId, startTime });
    const _consumption = consumption > 0 ? consumption : 0;
    if (existing) await ConsumptionIntervals.updateAsync(existing._id, { $set: { startTime, endTime, consumption: _consumption, generation, net } });
    else await ConsumptionIntervals.insertAsync({ meterId, startTime, endTime, consumption: _consumption, generation, net });
  }
};

/**
 * Returns the end time of the most recent interval for a meter, or its commissioning date.
 * @param meter - consumption meter
 * @returns latest interval end time or the meter commissioning date
 */
export const lastConsumptionIntervalTime = async (meter: ConsumptionMeter) => {
  const meterId = meter._id;
  const intervals = await ConsumptionIntervals.find({ meterId }, { sort: { startTime: -1 }, limit: 1 }).fetchAsync();
  const interval = intervals[0];
  return interval?.endTime || meter.commissioningDate;
};

/**
 * Finds missing time ranges across a sorted run of 15-minute intervals.
 * @param intervals - intervals sorted by start time
 * @param fromDate - range start
 * @param toDate - range end
 * @returns list of [start, end] gap pairs
 */
export const findConsumptionIntervalGaps = async (intervals: ConsumptionInterval[], fromDate: Date, toDate: Date) => {
  if (intervals.length === 0) return [[fromDate, toDate]];
  const gaps = [];
  let cursor = intervals[0];
  for (const interval of intervals.slice(1)) {
    const nextStartTime = new Date(cursor.startTime.getTime());
    nextStartTime.setMinutes(nextStartTime.getMinutes() + 15);
    if (interval.startTime.getTime() !== nextStartTime.getTime()) gaps.push([cursor.endTime, interval.startTime]);
    cursor = interval;
  }
  const lastInterval = intervals[intervals.length - 1];
  if (lastInterval.endTime.getTime() < toDate.getTime()) gaps.push(lastInterval.endTime, toDate);
  return gaps as [Date, Date][];
};

if (Meteor.isServer) {
  ConsumptionIntervals.createIndex({ meterId: 1, startTime: 1 });
  ConsumptionIntervals.createIndex({ startTime: 1 });
}

export interface CachedConsumptionInterval extends Doc {
  meterId: string;
  startDate: string;
  endDate: string;
  consumption: number;
  generation?: number;
  net?: number;
}
export const ConsumptionIntervalsDaily = new Mongo.Collection<CachedConsumptionInterval>('consumptionIntervalsDaily');
export const ConsumptionIntervalsMonthly = new Mongo.Collection<CachedConsumptionInterval>('consumptionIntervalsMonthly');

export const cacheAggregateConsumptionIntervals = DeclareMethod<{ meterIds?: string[], startDate: string }, void>('cacheAggregateConsumptionIntervals');

if (Meteor.isServer) {
  ConsumptionIntervalsDaily.createIndex({ meterId: 1, startDate: 1 }, { unique: true });
  ConsumptionIntervalsDaily.createIndex({ startDate: 1 });
  ConsumptionIntervalsMonthly.createIndex({ meterId: 1, startDate: 1 }, { unique: true });
  ConsumptionIntervalsMonthly.createIndex({ startDate: 1 });
}